import React from 'react';
import { Box } from '@mui/material';
import { keyframes } from '@emotion/react';

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.4; }
`;

// Exact Tailwind equivalents for the instance-monitor StatusBadge
const BADGE_STYLES = {
  green: {
    bg:   '#f0fdf4',  // green-50
    text: '#15803d',  // green-700
    ring: '#bbf7d0',  // green-200
    dot:  '#22c55e',  // green-500
  },
  red: {
    bg:   '#fef2f2',  // red-50
    text: '#b91c1c',  // red-700
    ring: '#fecaca',  // red-200
    dot:  '#ef4444',  // red-500
  },
  'N/A': {
    bg:   '#f1f5f9',  // slate-100
    text: '#475569',  // slate-600
    ring: '#e2e8f0',  // slate-200
    dot:  '#94a3b8',  // slate-400
  },
  unknown: {
    bg:   '#f1f5f9',
    text: '#475569',
    ring: '#e2e8f0',
    dot:  '#94a3b8',
  },
};

const STATUS_LABELS = {
  green:   'Online',
  red:     'Offline',
  'N/A':   'N/A',
  unknown: 'Unknown',
};

/**
 * HubStatusBadge — pixel-faithful port of instance-monitor's StatusBadge.tsx.
 *
 * variant="badge" (default): pill with inner dot + text label, ring border
 * variant="dot":             small coloured dot, pulsing when red
 *
 * @param {{ status: 'green'|'red'|'N/A'|'unknown', variant: 'badge'|'dot', size?: 'small'|'medium' }} props
 */
const HubStatusBadge = ({ status = 'unknown', variant = 'badge', size = 'small' }) => {
  const s = BADGE_STYLES[status] || BADGE_STYLES.unknown;
  const isRed = status === 'red';

  if (variant === 'dot') {
    const sz = size === 'medium' ? 12 : 10;
    return (
      <Box
        title={STATUS_LABELS[status]}
        sx={{
          display: 'inline-block',
          width:  sz,
          height: sz,
          borderRadius: '50%',
          bgcolor: s.dot,
          flexShrink: 0,
          ...(isRed && { animation: `${pulse} 1.5s ease-in-out infinite` }),
        }}
      />
    );
  }

  // Badge mode: `inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1`
  return (
    <Box
      component="span"
      title={STATUS_LABELS[status]}
      sx={{
        display:     'inline-flex',
        alignItems:  'center',
        gap:         '6px',
        borderRadius: '9999px',
        px: '10px',
        py: '2px',
        fontSize:    '0.72rem',
        fontWeight:  600,
        bgcolor:     s.bg,
        color:       s.text,
        boxShadow:   `0 0 0 1px ${s.ring}`,
        userSelect:  'none',
      }}
    >
      <Box
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: s.dot,
          flexShrink: 0,
          ...(isRed && { animation: `${pulse} 1.5s ease-in-out infinite` }),
        }}
      />
      {STATUS_LABELS[status]}
    </Box>
  );
};

export default HubStatusBadge;
